// Desktop Threads tab — detail view.
//
// Route: /threads/:threadId. Mirrors the iOS app's Thread Detail screen:
//   Section A — Recordings list (conversations under this thread)
//   Section B — Gee Coaching Analysis (latest committed run for workflow=GEE)
//
// The analysis render delegates to <GeeCoachingReport />, the same component
// used by the iOS WebView embed (`/embed/thread/:id/analysis`). The fetch
// + tolerance code (analysis_json | analysis | result | run) is copy-paste
// from `EmbedAnalysisView` so the two surfaces stay in lockstep.

import React, { useEffect, useState, useCallback, useReducer } from "react";
import {
  Box,
  Flex,
  HStack,
  VStack,
  Text,
  Heading,
  Spinner,
  Button,
  Badge,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Divider,
  Collapse,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useToast,
} from "@chakra-ui/react";
import { ChevronLeftIcon } from "@chakra-ui/icons";
import { useNavigate, useParams } from "react-router-dom";
import PageLayout from "./PageLayout";
import GeeCoachingReport from "./GeeCoachingReport";
import CoachingReportView from "./CoachingReportView";
import ConversationLifecycleBar from "./ConversationLifecycleBar";
import {
  getThread,
  getLatestCommittedAnalysisRun,
  listAnalysisRuns,
  triggerManualAnalysis,
  triggerManualRetag,
  buildThreadEventsUrl,
  getCurrentCoachingReport,
} from "../services/embed";
import { currentEmbedToken } from "../EmbedAuthProvider";
import { formatDate } from "../utils/dateUtils";

// Events that should trigger a re-fetch of the thread + analysis. iOS
// already consumes these (see MicAppIOS/App/Services/SSEClient.swift); this
// list intentionally mirrors that so both surfaces stay in lockstep.
const THREAD_DETAIL_REFRESH_EVENTS = [
  "raw_transcript_emitted",
  "tagged_transcript_ready",
  "cleaned_transcript_ready",
  "batch_processing_failed",
  "analysis_starting",
  "analysis_started",
  "analysis_complete",
  "analysis_failed",
  "thread_analysis_failed",
];

// Track B PR3 — events that drive the page-level useReducer (per-conversation
// lifecycle hints + the analysis banner). The reducer is purely additive on
// top of the refetch behavior; the page still re-fetches on the same set of
// events, but the banner + per-row lifecycle bar are now driven without
// waiting for the round-trip.
const THREAD_DETAIL_SSE_EVENTS = [
  ...THREAD_DETAIL_REFRESH_EVENTS,
  "batch_transcription_started",
  "batch_transcription_complete",
  "batch_tagged_started",
  "batch_tagged_failed",
  "batch_cleaned_started",
  "batch_cleaned_failed",
  "recording_state_changed",
  "analysis_progress",
];

// Initial reducer state. `conversations` is keyed by conversation id —
// each slice tracks the latest known recording_state so the per-row
// lifecycle bar can react to `recording_state_changed` without re-fetching.
// `analysis` tracks the "in-flight banner" + the latest progress hint.
const INITIAL_SSE_STATE = {
  conversations: {},
  analysis: {
    running: false,
    startedAt: null,
    phase: null,
    current: 0,
    total: 0,
    error: null,
  },
};

/**
 * Track B PR3 — page-level reducer.
 *
 * Exported so tests can drive it directly and so future work (V0.1 iOS
 * mirror) can share the contract.
 *
 * Recognised event types:
 *   - recording_state_changed   → conversations[cid].recording_state
 *   - analysis_started          → analysis.running = true (+ startedAt)
 *   - analysis_progress         → analysis.phase/current/total
 *   - analysis_complete         → analysis.running = false (success)
 *   - analysis_failed           → analysis.running = false (+ error)
 *
 * Anything else is a no-op so the reducer is forward-compatible with
 * future Track B event additions.
 */
export function threadDetailReducer(state, event) {
  if (!event || typeof event !== "object") return state;
  const t = event.type;
  switch (t) {
    case "recording_state_changed": {
      const cid = event.conversation_id;
      const newState = event?.payload?.recording_state;
      if (cid == null || !newState) return state;
      const prev = state.conversations[cid] || {};
      return {
        ...state,
        conversations: {
          ...state.conversations,
          [cid]: { ...prev, recording_state: newState },
        },
      };
    }
    case "analysis_started":
    case "analysis_starting":
      return {
        ...state,
        analysis: {
          running: true,
          startedAt: event?.occurred_at || new Date().toISOString(),
          phase: null,
          current: 0,
          total: 0,
          error: null,
        },
      };
    case "analysis_progress": {
      const phase = event?.payload?.phase ?? state.analysis.phase;
      const current = event?.payload?.current ?? state.analysis.current;
      const total = event?.payload?.total ?? state.analysis.total;
      return {
        ...state,
        analysis: { ...state.analysis, phase, current, total, running: true },
      };
    }
    case "analysis_complete":
      return {
        ...state,
        analysis: { ...INITIAL_SSE_STATE.analysis },
      };
    case "analysis_failed":
    case "thread_analysis_failed":
      return {
        ...state,
        analysis: {
          ...INITIAL_SSE_STATE.analysis,
          error: event?.payload?.reason || event?.payload?.error || "failed",
        },
      };
    default:
      return state;
  }
}

const defaultEventSourceFactory = (url) =>
  typeof window !== "undefined" && window.EventSource
    ? new window.EventSource(url)
    : null;

// Same shape-tolerance helper used by EmbedAnalysisView: backend may return
// `{items}`, `{runs}`, a bare list, or a single object.
function extractLatestRun(response) {
  const items =
    response?.items ||
    response?.runs ||
    (Array.isArray(response) ? response : null) ||
    (response ? [response] : []);
  return items[0] || null;
}

// The thread_analysis_runs row stores the analyzer JSON in `analysis_json`
// on the backend; the API serialiser may expose it as `analysis` or `result`
// historically. Tolerate all.
function unwrapAnalysisJson(run) {
  if (!run) return null;
  return run.analysis_json ?? run.analysis ?? run.result ?? run;
}

// A "real" analysis: non-empty object with at least one observation or a
// summary > 0 chars. Empty `{}` or a stub-only blob shows the not-ready
// banner instead of an empty report.
function isAnalysisReady(analysisJson) {
  if (!analysisJson || typeof analysisJson !== "object") return false;
  if (Array.isArray(analysisJson?.coaching_observations) &&
      analysisJson.coaching_observations.length > 0) {
    return true;
  }
  if (typeof analysisJson?.summary === "string" &&
      analysisJson.summary.trim().length > 0) {
    return true;
  }
  if (typeof analysisJson?.interaction_summary === "string" &&
      analysisJson.interaction_summary.trim().length > 0) {
    return true;
  }
  return false;
}

const RecordingRow = ({ conversation, onOpen, sseSlice, onRetag, retagging }) => {
  // Track B PR3 — prefer the SSE-driven recording_state (if any has been
  // observed since page load) over the snapshot from the last REST fetch.
  const recordingState =
    sseSlice?.recording_state || conversation.recording_state;
  // The Re-tag affordance only makes sense for finalized recordings —
  // mid-stream conversations have their TAGGED items still being written,
  // and the backend will 422 anyway. Hide the control instead of
  // showing-then-failing.
  const canRetag = recordingState === "ENDED";
  return (
    <Tr
      data-testid={`recording-row-${conversation.id}`}
      cursor="pointer"
      onClick={() => onOpen(conversation.id)}
      borderBottomWidth="1px"
      borderBottomColor="gray.100"
      _hover={{ bg: "gray.50" }}
      transition="background 0.1s"
    >
      <Td py={3} px={4}>
        <Text fontSize="xs" color="gray.400" fontFamily="mono">
          {conversation.id}
        </Text>
      </Td>
      <Td py={3} px={4}>
        <Text fontSize="sm">{formatDate(conversation.created_at)}</Text>
      </Td>
      <Td py={3} px={4}>
        {recordingState ? (
          <Badge colorScheme="blue" variant="subtle" fontSize="xs">
            {recordingState}
          </Badge>
        ) : (
          <Text fontSize="sm" color="gray.400">—</Text>
        )}
      </Td>
      <Td py={3} px={4}>
        {conversation.pipeline_state ? (
          <Badge colorScheme="purple" variant="subtle" fontSize="xs">
            {conversation.pipeline_state}
          </Badge>
        ) : (
          <Text fontSize="sm" color="gray.400">—</Text>
        )}
      </Td>
      <Td py={3} px={4} minW="120px">
        <ConversationLifecycleBar
          size="sm"
          recordingState={recordingState}
          analysisCommitted={
            (conversation.pipeline_state || "").toUpperCase() === "ANALYZED"
          }
        />
      </Td>
      <Td py={3} px={4} textAlign="right">
        {canRetag ? (
          <Button
            size="xs"
            variant="outline"
            colorScheme="orange"
            isLoading={retagging}
            onClick={(e) => {
              // The row itself is clickable (navigates to the conversation
              // detail page) — stop propagation so the Re-tag click doesn't
              // also fire the row's onOpen.
              e.stopPropagation();
              onRetag(conversation.id);
            }}
            data-testid={`recording-retag-button-${conversation.id}`}
          >
            Re-tag
          </Button>
        ) : null}
      </Td>
    </Tr>
  );
};

const RecordingsSection = ({
  conversations,
  onOpenConversation,
  sseConversations,
  onRequestRetag,
  retaggingConversationId,
}) => (
  <Box data-testid="recordings-section">
    <Heading size="md" mb={3}>
      Recordings
    </Heading>
    {conversations.length === 0 ? (
      <Box
        py={10}
        textAlign="center"
        bg="gray.50"
        borderRadius="lg"
        borderWidth="1px"
        borderColor="gray.200"
        borderStyle="dashed"
        data-testid="recordings-empty"
      >
        <Text color="gray.500" fontSize="sm">
          No recordings in this thread yet.
        </Text>
      </Box>
    ) : (
      <Box
        overflowX="auto"
        borderRadius="lg"
        borderWidth="1px"
        borderColor="gray.200"
        bg="white"
      >
        <Table variant="unstyled" size="sm">
          <Thead>
            <Tr bg="gray.50" borderBottomWidth="1px" borderBottomColor="gray.200">
              <Th py={3} px={4} fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider">
                ID
              </Th>
              <Th py={3} px={4} fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider">
                Created
              </Th>
              <Th py={3} px={4} fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider">
                Recording
              </Th>
              <Th py={3} px={4} fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider">
                Pipeline
              </Th>
              <Th py={3} px={4} fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider">
                Lifecycle
              </Th>
              <Th py={3} px={4} fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider" textAlign="right">
                Actions
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {conversations.map((c) => (
              <RecordingRow
                key={c.id}
                conversation={c}
                onOpen={onOpenConversation}
                sseSlice={sseConversations?.[c.id]}
                onRetag={onRequestRetag}
                retagging={retaggingConversationId === c.id}
              />
            ))}
          </Tbody>
        </Table>
      </Box>
    )}
  </Box>
);

// Track C — input-snapshot panel rendered under the Analysis header.
// Source of truth: ``latestRun.input_snapshot_json``, mirroring the
// shape produced by ``POST /threads/{id}/analyze``:
//   { conversations: [{id, recording_state, cleaned_item_ids, ready}],
//     total_cleaned_items, total_audio_ms }
const InputsPanel = ({ snapshot, isOpen }) => {
  if (!snapshot || !Array.isArray(snapshot.conversations)) return null;
  return (
    <Collapse in={isOpen} animateOpacity>
      <Box
        mt={2}
        p={3}
        bg="gray.50"
        borderRadius="md"
        borderWidth="1px"
        borderColor="gray.200"
        data-testid="analysis-inputs-panel"
      >
        <Text fontSize="xs" color="gray.500" mb={2}>
          {snapshot.conversations.length} conversation
          {snapshot.conversations.length === 1 ? "" : "s"} ·{" "}
          {snapshot.total_cleaned_items || 0} cleaned item
          {(snapshot.total_cleaned_items || 0) === 1 ? "" : "s"}
        </Text>
        <Table size="sm" variant="simple">
          <Thead>
            <Tr>
              <Th px={2} py={1} fontSize="xs">Conv #</Th>
              <Th px={2} py={1} fontSize="xs">State</Th>
              <Th px={2} py={1} fontSize="xs">Cleaned</Th>
              <Th px={2} py={1} fontSize="xs">Ready</Th>
            </Tr>
          </Thead>
          <Tbody>
            {snapshot.conversations.map((c) => (
              <Tr
                key={c.id}
                opacity={c.ready ? 1 : 0.5}
                data-testid={`inputs-row-${c.id}`}
                data-ready={c.ready ? "true" : "false"}
              >
                <Td px={2} py={1} fontSize="xs">{c.id}</Td>
                <Td px={2} py={1} fontSize="xs">{c.recording_state}</Td>
                <Td px={2} py={1} fontSize="xs">
                  {Array.isArray(c.cleaned_item_ids)
                    ? c.cleaned_item_ids.length
                    : 0}
                </Td>
                <Td px={2} py={1} fontSize="xs">
                  {c.ready ? (
                    <Badge colorScheme="green" fontSize="2xs">ready</Badge>
                  ) : (
                    <Badge colorScheme="gray" fontSize="2xs">not yet</Badge>
                  )}
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
    </Collapse>
  );
};

const AnalysisSection = ({
  analysisRun,
  latestRun,
  threadId,
  currentCoachingReport,
  onRetry,
  onReanalyze,
  reanalyzing,
}) => {
  const analysisJson = unwrapAnalysisJson(analysisRun);
  const ready = isAnalysisReady(analysisJson);
  // Track C lifecycle bar — "ANALYZING" lights up iff the most recent run
  // (any workflow_key) is queued or running. Latest-of-any-status is
  // sourced from ``listAnalysisRuns``; the committed-only ``analysisRun``
  // is the displayed report.
  const latestStatus = (latestRun?.status || "").toLowerCase();
  const isAnalyzing =
    latestStatus === "queued" ||
    latestStatus === "started" ||
    latestStatus === "running";

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [inputsOpen, setInputsOpen] = useState(false);

  // Display attribution: prefer the latest manual run's snapshot, else
  // fall back to the committed run's snapshot (the workflow now writes
  // it INTO the row on commit).
  const snapshotSourceRun = latestRun || analysisRun || null;
  const snapshot = snapshotSourceRun?.input_snapshot_json || null;
  const triggeredBy = snapshotSourceRun?.triggered_by || null;
  const triggeredByUserId = snapshotSourceRun?.triggered_by_user_id || null;
  const lastAnalyzedAt =
    analysisRun?.committed_at ||
    analysisRun?.created_at ||
    snapshotSourceRun?.created_at ||
    null;

  return (
    <Box data-testid="analysis-section">
      <Flex align="center" mb={3} justify="space-between" wrap="wrap" gap={2}>
        <HStack spacing={3} align="center">
          <Heading size="md">Gee Analysis</Heading>
          {isAnalyzing ? (
            <HStack spacing={2} data-testid="analysis-analyzing-indicator">
              <Spinner size="xs" />
              <Text fontSize="xs" color="gray.500">
                Analyzing…
              </Text>
            </HStack>
          ) : lastAnalyzedAt ? (
            <Text fontSize="xs" color="gray.500" data-testid="analysis-last-meta">
              Last analyzed {formatDate(lastAnalyzedAt)}
              {triggeredBy === "manual" && triggeredByUserId
                ? ` · Triggered by user ${triggeredByUserId}`
                : triggeredBy === "automatic"
                  ? " · Triggered automatically"
                  : ""}
            </Text>
          ) : null}
        </HStack>
        <HStack spacing={2}>
          {snapshot ? (
            <Button
              size="xs"
              variant="outline"
              onClick={() => setInputsOpen((v) => !v)}
              data-testid="analysis-toggle-inputs"
            >
              {inputsOpen ? "Hide inputs" : "Show inputs"}
            </Button>
          ) : null}
          <Button
            size="xs"
            colorScheme="blue"
            onClick={() => setConfirmOpen(true)}
            isDisabled={isAnalyzing || reanalyzing}
            data-testid="analysis-reanalyze-button"
          >
            Re-analyze
          </Button>
        </HStack>
      </Flex>

      <InputsPanel snapshot={snapshot} isOpen={inputsOpen} />

      {/* analysis-v2 coaching report — sourced from the current
          `report:coaching` thread fact rather than a legacy analysis-run. */}
      {currentCoachingReport ? (
        <Box mb={6} data-testid="current-coaching-report-section">
          <CoachingReportView
            analysis={currentCoachingReport}
            threadId={threadId}
          />
        </Box>
      ) : null}

      <Modal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        size="sm"
      >
        <ModalOverlay />
        <ModalContent data-testid="reanalyze-confirm-modal">
          <ModalHeader fontSize="md">Re-analyze this thread?</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="sm">
              This starts a new analysis using all finalized conversations.
              The previous result remains visible until this one completes.
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button
              size="sm"
              variant="ghost"
              mr={2}
              onClick={() => setConfirmOpen(false)}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              colorScheme="blue"
              onClick={async () => {
                setConfirmOpen(false);
                await onReanalyze();
              }}
              isLoading={reanalyzing}
              data-testid="reanalyze-confirm-button"
            >
              Re-analyze
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {ready ? (
        <GeeCoachingReport analysis={analysisJson} threadId={threadId} />
      ) : (
        <Box
          py={10}
          px={6}
          textAlign="center"
          bg="gray.50"
          borderRadius="lg"
          borderWidth="1px"
          borderColor="gray.200"
          data-testid="analysis-not-ready"
        >
          <Text color="gray.600" fontSize="sm" mb={3}>
            {isAnalyzing
              ? "Analysis in progress. The page will refresh when it lands."
              : "Analysis not ready yet. Recordings are still being analyzed."}
          </Text>
          <Button size="xs" onClick={onRetry}>
            Check again
          </Button>
        </Box>
      )}
    </Box>
  );
};

const ThreadDetailPage = () => {
  const { threadId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [thread, setThread] = useState(null);
  const [analysisRun, setAnalysisRun] = useState(null);
  // analysis-v2 coaching report — sourced from the current `report:coaching`
  // thread fact (not an analysis-run). Null until the surviving fact resolves.
  const [currentCoachingReport, setCurrentCoachingReport] = useState(null);
  // Track B PR3 — page-level SSE reducer (per-conversation slices +
  // global analysis-banner state).
  const [sseState, dispatchSse] = useReducer(
    threadDetailReducer,
    INITIAL_SSE_STATE,
  );
  // Track C — latest analysis run regardless of status (queued / running
  // / committed / failed). Drives the "Analyzing…" indicator and the
  // input-snapshot panel.
  const [latestRun, setLatestRun] = useState(null);
  const [reanalyzing, setReanalyzing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Manual re-tag flow — confirm modal + per-conversation in-flight flag.
  // ``retagPendingId`` is the conv id awaiting confirmation; once the user
  // confirms, ``retaggingId`` flips to the same value until POST resolves.
  const [retagPendingId, setRetagPendingId] = useState(null);
  const [retaggingId, setRetaggingId] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [threadResp, runResp, anyStatusResp, coachingResp] =
        await Promise.all([
          getThread(threadId),
          // Match existing EmbedAnalysisView signature — pass workflowKey
          // positionally; the service accepts a string.
          getLatestCommittedAnalysisRun(threadId, "GEE"),
          // Lightweight: latest 3 runs regardless of status.
          listAnalysisRuns(threadId, { limit: 3 }).catch(() => null),
          // analysis-v2 coaching report (report:coaching thread fact). Tolerate
          // failure so the rest of the page still loads if facts 404/500.
          getCurrentCoachingReport(threadId).catch(() => null),
        ]);
      setThread(threadResp || null);
      setAnalysisRun(extractLatestRun(runResp));
      setLatestRun(extractLatestRun(anyStatusResp));
      setCurrentCoachingReport(coachingResp || null);
    } catch (e) {
      const message =
        e?.response?.data?.detail || e?.message || "Failed to load thread.";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [threadId]);

  // Track C — POST /threads/{id}/analyze, toast on success/429, then
  // re-fetch so the queued row + snapshot surface immediately (SSE will
  // refresh again once the workflow completes).
  const handleReanalyze = useCallback(async () => {
    setReanalyzing(true);
    try {
      const resp = await triggerManualAnalysis(threadId);
      const summary = resp?.snapshot_summary;
      toast({
        title: "Analysis started",
        description: summary
          ? `${summary.ready_count}/${summary.total_conversations} conversations, ${summary.total_cleaned_items} cleaned items.`
          : undefined,
        status: "success",
        duration: 4000,
        isClosable: true,
      });
      await fetchAll();
    } catch (e) {
      if (e?.response?.status === 429) {
        const retryAfter =
          e?.response?.headers?.["retry-after"] ||
          e?.response?.headers?.["Retry-After"] ||
          "30";
        toast({
          title: "Re-analyze rate-limited",
          description: `Please try again in ${retryAfter}s.`,
          status: "warning",
          duration: 5000,
          isClosable: true,
        });
      } else {
        const message =
          e?.response?.data?.detail || e?.message || "Re-analyze failed.";
        toast({
          title: "Re-analyze failed",
          description: message,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } finally {
      setReanalyzing(false);
    }
  }, [threadId, toast, fetchAll]);

  // Manual re-tag — staged two-step: opening the modal stores the conv id
  // in ``retagPendingId``; the modal's confirm button calls this with the
  // same id to actually fire the POST. The existing per-thread SSE
  // subscription already refreshes on tagged_transcript_ready /
  // cleaned_transcript_ready / analysis_complete so no extra wiring is
  // needed for the UI to catch up.
  const confirmRetag = useCallback(async (conversationId) => {
    setRetaggingId(conversationId);
    try {
      await triggerManualRetag(conversationId);
      toast({
        title: "Re-tagging started",
        description: `Conversation ${conversationId} will refresh as the pipeline re-runs.`,
        status: "success",
        duration: 4000,
        isClosable: true,
      });
    } catch (e) {
      if (e?.response?.status === 429) {
        const retryAfter =
          e?.response?.headers?.["retry-after"] ||
          e?.response?.headers?.["Retry-After"] ||
          "60";
        toast({
          title: "Re-tag rate-limited",
          description: `Please try again in ${retryAfter}s.`,
          status: "warning",
          duration: 5000,
          isClosable: true,
        });
      } else {
        const message =
          e?.response?.data?.detail || e?.message || "Re-tag failed.";
        toast({
          title: "Re-tag failed",
          description: message,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } finally {
      setRetaggingId(null);
    }
  }, [toast]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Subscribe to per-thread SSE so the page refreshes as the
  // BulkImportWorkflow (or live recording) drives transcription, tagging,
  // scrubbing, and analysis through the pipeline. Refresh-only — no fancy
  // optimistic merging; the page is small enough that a re-fetch is fine
  // for v1. The /threads/{id}/events endpoint is already wired for iOS;
  // we just lean on the same stream here.
  useEffect(() => {
    if (!threadId) return undefined;
    const token = currentEmbedToken?.() || "";
    const url = buildThreadEventsUrl(threadId, token);
    let es;
    try {
      es = defaultEventSourceFactory(url);
    } catch (_e) {
      return undefined;
    }
    if (!es) return undefined;

    let refetchScheduled = false;
    const scheduleRefetch = () => {
      // Coalesce bursts (e.g. raw + tagged + cleaned land back-to-back) into
      // a single fetch by deferring one tick.
      if (refetchScheduled) return;
      refetchScheduled = true;
      setTimeout(() => {
        refetchScheduled = false;
        fetchAll().catch(() => {
          // Swallow — error state stays sticky from the last fetch.
        });
      }, 250);
    };

    // Two-layer SSE handler:
    //   (a) parse the envelope and dispatch into the page-level reducer
    //       so the per-conversation lifecycle bar + the analysis banner
    //       react immediately (no round-trip required).
    //   (b) schedule a coalesced refetch on events that change the
    //       backend's authoritative state.
    const onMessage = (rawEvent) => {
      let envelope = null;
      try {
        envelope =
          typeof rawEvent?.data === "string"
            ? JSON.parse(rawEvent.data)
            : null;
      } catch (_e) {
        envelope = null;
      }
      if (envelope) {
        dispatchSse(envelope);
      }
      if (
        !envelope ||
        THREAD_DETAIL_REFRESH_EVENTS.includes(envelope.type)
      ) {
        scheduleRefetch();
      }
    };

    if (typeof es.addEventListener === "function") {
      es.addEventListener("message", onMessage);
      THREAD_DETAIL_SSE_EVENTS.forEach((type) =>
        es.addEventListener(type, onMessage),
      );
    } else {
      es.onmessage = onMessage;
    }

    return () => {
      try {
        es.close();
      } catch (_e) {
        // ignore
      }
    };
  }, [threadId, fetchAll]);

  const retryAnalysis = useCallback(async () => {
    try {
      const runResp = await getLatestCommittedAnalysisRun(threadId, "GEE");
      setAnalysisRun(extractLatestRun(runResp));
    } catch (e) {
      // Surface as a toast-less error in the not-ready banner — caller can
      // re-click. Keeping this lightweight; full toast wiring is overkill
      // for a manual retry button.
      console.error("[ThreadDetailPage] retry failed:", e?.message);
    }
  }, [threadId]);

  const handleOpenConversation = (conversationId) => {
    // Per-recording detail page (transcript + metadata). The legacy
    // `/analysis/:conversation_id` route still exists for the
    // Demo/UNTUCKit/Arcteryx flows but it renders customer-specific
    // analysis renderers that aren't right for Toyota/Gee data — so the
    // Threads tab links to the new shared `ConversationDetailPage` instead.
    navigate(`/conversations/${conversationId}`);
  };

  return (
    <PageLayout>
      <VStack spacing={6} align="stretch" p={6} data-testid="thread-detail-page">
        {/* Back link + title */}
        <Flex align="center" gap={2}>
          <Button
            size="sm"
            variant="ghost"
            leftIcon={<ChevronLeftIcon />}
            onClick={() => navigate("/threads")}
            data-testid="thread-back-button"
          >
            Threads
          </Button>
          {thread && (
            <HStack spacing={3}>
              <Heading size="lg">{thread.title || "Untitled"}</Heading>
              {thread.archived && (
                <Badge colorScheme="gray">Archived</Badge>
              )}
            </HStack>
          )}
        </Flex>

        {loading && (
          <Flex justify="center" py={16}>
            <Spinner size="lg" />
          </Flex>
        )}

        {!loading && error && (
          <Alert status="error" borderRadius="md" data-testid="thread-error">
            <AlertIcon />
            <Box flex={1}>
              <AlertTitle fontSize="sm">Could not load thread</AlertTitle>
              <AlertDescription fontSize="xs">{error}</AlertDescription>
            </Box>
            <Button size="xs" onClick={fetchAll}>
              Retry
            </Button>
          </Alert>
        )}

        {!loading && !error && thread && (
          <>
            {sseState.analysis.running && (
              <Alert
                status="info"
                borderRadius="md"
                data-testid="analysis-banner"
              >
                <AlertIcon />
                <Box flex={1}>
                  <AlertTitle fontSize="sm">
                    Analyzing…
                    {sseState.analysis.phase
                      ? ` (phase: ${sseState.analysis.phase}`
                      : ""}
                    {sseState.analysis.total
                      ? `${
                          sseState.analysis.phase ? ", " : " ("
                        }${sseState.analysis.current}/${
                          sseState.analysis.total
                        })`
                      : sseState.analysis.phase
                        ? ")"
                        : ""}
                  </AlertTitle>
                </Box>
              </Alert>
            )}
            <RecordingsSection
              conversations={thread.conversations || []}
              onOpenConversation={handleOpenConversation}
              sseConversations={sseState.conversations}
              onRequestRetag={(cid) => setRetagPendingId(cid)}
              retaggingConversationId={retaggingId}
            />
            <Divider />
            <AnalysisSection
              analysisRun={analysisRun}
              latestRun={latestRun}
              threadId={threadId}
              currentCoachingReport={currentCoachingReport}
              onRetry={retryAnalysis}
              onReanalyze={handleReanalyze}
              reanalyzing={reanalyzing}
            />

            {/* Re-tag confirmation modal — staged so an accidental row
                click can't fire the destructive DELETE. */}
            <Modal
              isOpen={retagPendingId !== null}
              onClose={() => setRetagPendingId(null)}
              size="sm"
            >
              <ModalOverlay />
              <ModalContent data-testid="retag-confirm-modal">
                <ModalHeader fontSize="md">Re-tag this recording?</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                  <Text fontSize="sm">
                    Existing TAGGED + CLEANED transcripts will be regenerated,
                    and analysis will re-run automatically. The previous
                    analysis result remains visible until the new one lands.
                  </Text>
                </ModalBody>
                <ModalFooter>
                  <Button
                    size="sm"
                    variant="ghost"
                    mr={2}
                    onClick={() => setRetagPendingId(null)}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    colorScheme="orange"
                    onClick={async () => {
                      const cid = retagPendingId;
                      setRetagPendingId(null);
                      await confirmRetag(cid);
                    }}
                    isLoading={retaggingId !== null}
                    data-testid="retag-confirm-button"
                  >
                    Re-tag
                  </Button>
                </ModalFooter>
              </ModalContent>
            </Modal>
          </>
        )}
      </VStack>
    </PageLayout>
  );
};

export default ThreadDetailPage;
